import { Anchor, Group, useMantineTheme } from '@mantine/core';
import { Text } from '@mantine/core';
import { useTeam } from '@vrplatform/data';
import { day, type Maybe } from '@vrplatform/utils';
import { Banner } from '@vrplatform/components';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@vrplatform/icons';

type Props = {
  date: Maybe<string>;
};

export function useIsBooksClosed(date: Maybe<string>) {
  const [{ booksClosedAt }] = useTeam();

  return useMemo(() => {
    return (
      !!booksClosedAt &&
      !!date &&
      day(date).isValid() &&
      day(booksClosedAt).isValid() &&
      (day(date).isBefore(booksClosedAt, 'day') ||
        day(date).isSame(booksClosedAt, 'day'))
    );
  }, [date, booksClosedAt]);
}

export const BooksClosedBanner = ({ date }: Props) => {
  const [{ booksClosedAt }] = useTeam();

  const theme = useMantineTheme();

  const isBooksClosed = useIsBooksClosed(date);

  return (
    <Banner
      mounted={!!isBooksClosed}
      type="warning"
      shadow={null}
      icon="Lock2Icon"
      borderColor={theme.colors.orange[1]}
    >
      <Group wrap="nowrap">
        <Icon
          icon="Lock2Icon"
          color={(theme) => theme.colors.orange[7]}
          size={20}
        />
        <Text fw={500} size="md">
          Books are closed through {day(booksClosedAt).format('ll')}. Edits are
          disabled; adjust the closing date in{' '}
          <Anchor
            component={Link}
            to="/settings/accounting/books-closed"
            inherit
            c={theme.black}
            underline="always"
            sx={{
              textUnderlineOffset: 4,
              textDecorationThickness: 0.5,
            }}
          >
            Settings &gt; Accounting &gt; Books Closed Date
          </Anchor>
          .
        </Text>
      </Group>
    </Banner>
  );
};
